import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import { PMDialog } from "../../../utils/PMDialog";
import { fetchServiceAgreementSetup } from "../../../../api/service/agreement";

const STEP_KEYS = ["agreement_types", "report_types", "linked_report_type"];

export function ServiceAgreementSetupGuide({
  open,
  onClose,
  setup: setupProp,
  blockCreate = false,
}) {
  const { t } = useTranslation();
  const [setup, setSetup] = useState(setupProp || null);
  const [tab, setTab] = useState(blockCreate ? "setup" : "guide");

  useEffect(() => {
    if (!open) return;
    if (setupProp) {
      setSetup(setupProp);
      setTab(blockCreate || setupProp.ready === false ? "setup" : "guide");
      return;
    }
    fetchServiceAgreementSetup()
      .then((data) => {
        setSetup(data);
        setTab(blockCreate || data?.ready === false ? "setup" : "guide");
      })
      .catch(() => setSetup(null));
  }, [open, setupProp, blockCreate]);

  const steps = setup?.steps || [];
  const ready = setup?.ready === true;
  const blocking = blockCreate || !ready;

  return (
    <PMDialog
      open={open}
      onClose={onClose}
      size="3xl"
      title={t("service.agreements.guide.title")}
    >
      <div className="space-y-5 text-left">
        {blocking ? (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
            {t("service.agreements.setup.blocked")}
          </div>
        ) : null}

        <div className="flex rounded-full bg-slate-100 p-1 text-sm font-semibold">
          <button
            type="button"
            onClick={() => setTab("setup")}
            className={`flex-1 rounded-full px-3 py-1.5 ${
              tab === "setup" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"
            }`}
          >
            {t("service.agreements.setup.tab")}
          </button>
          <button
            type="button"
            onClick={() => setTab("guide")}
            className={`flex-1 rounded-full px-3 py-1.5 ${
              tab === "guide" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"
            }`}
          >
            {t("service.agreements.guide.tab")}
          </button>
        </div>

        {tab === "setup" ? (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              {t("service.agreements.setup.intro")}
            </p>
            {STEP_KEYS.map((key) => {
              const step = steps.find((row) => row.key === key) || { key, done: false };
              return (
                <div
                  key={key}
                  className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4"
                >
                  {step.done ? (
                    <CheckCircleIcon className="mt-0.5 h-6 w-6 shrink-0 text-emerald-600" />
                  ) : (
                    <ExclamationTriangleIcon className="mt-0.5 h-6 w-6 shrink-0 text-amber-500" />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-slate-900">
                      {t(`service.agreements.setup.steps.${key}.title`)}
                    </p>
                    <p className="mt-0.5 text-sm text-slate-600">
                      {t(`service.agreements.setup.steps.${key}.body`)}
                    </p>
                    {typeof step.count === "number" ? (
                      <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-slate-400">
                        {t("service.agreements.setup.count", { count: step.count })}
                      </p>
                    ) : null}
                  </div>
                  <Link
                    to={step.href || "/dashboard/service/settings"}
                    onClick={onClose}
                    className="shrink-0 rounded-xl bg-[#1463C7] px-3 py-2 text-xs font-bold text-white hover:bg-[#0B3A78]"
                  >
                    {t("service.agreements.setup.open_settings")}
                  </Link>
                </div>
              );
            })}
          </div>
        ) : (
          <ol className="space-y-3 text-sm text-slate-700">
            {[
              "kind",
              "interval",
              "discount_lines",
              "address",
              "contacts",
              "orderlines",
            ].map((key) => (
              <li
                key={key}
                className="flex gap-3 rounded-2xl border border-slate-200 bg-slate-50/80 p-4"
              >
                <InformationCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-[#1463C7]" />
                <div>
                  <p className="font-semibold text-slate-900">
                    {t(`service.agreements.guide.items.${key}.title`)}
                  </p>
                  <p className="mt-1 text-slate-600">
                    {t(`service.agreements.guide.items.${key}.body`)}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        )}

        <div className="flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-700"
          >
            {t("common.close")}
          </button>
        </div>
      </div>
    </PMDialog>
  );
}
